import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useVenues } from '../contexts/VenueContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiTarget, FiNavigation, FiPlus, FiTrash2, FiSave, FiAlertCircle, FiTrendingUp } = FiIcons;

const VenuePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVenue, updateVenue } = useVenues();
  const { getEffectiveMaxLandmarks, subscription } = useSubscription();
  
  const venue = getVenue(id);
  const maxLandmarks = getEffectiveMaxLandmarks();
  
  const [editingSection, setEditingSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedVenue, setEditedVenue] = useState({
    nearby_landmarks: []
  });

  useEffect(() => {
    if (venue) {
      setEditedVenue({
        ...venue,
        nearby_landmarks: venue.nearby_landmarks || []
      });
    }
  }, [venue, editingSection]);

  const handleAddLandmark = () => {
    if (editedVenue.nearby_landmarks.length >= maxLandmarks) return;
    setEditedVenue(prev => ({
      ...prev,
      nearby_landmarks: [...prev.nearby_landmarks, { name: '', distance: '' }]
    }));
  };

  const handleLandmarkChange = (index, field, value) => {
    const newLandmarks = [...editedVenue.nearby_landmarks];
    newLandmarks[index][field] = value;
    setEditedVenue(prev => ({ ...prev, nearby_landmarks: newLandmarks }));
  };

  const removeLandmark = (index) => {
    setEditedVenue(prev => ({
      ...prev,
      nearby_landmarks: prev.nearby_landmarks.filter((_, i) => i !== index)
    }));
  };

  const saveLandmarks = async () => {
    setLoading(true);
    await updateVenue(id, { nearby_landmarks: editedVenue.nearby_landmarks });
    setEditingSection(null);
    setLoading(false);
  };

  if (!venue) return null;

  const landmarksCount = editedVenue.nearby_landmarks.length;
  const isAtLimit = landmarksCount >= maxLandmarks;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <SafeIcon icon={FiArrowLeft} className="h-6 w-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{venue.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-wider">{subscription?.plan.name} Plan</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LANDMARKS SECTION */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiTarget} className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-bold text-gray-900">Repere & SEO Local</h2>
              </div>
              
              {editingSection === 'landmarks' ? (
                <div className="flex gap-3">
                  <button onClick={() => setEditingSection(null)} className="text-gray-500 text-sm font-medium">Anulează</button>
                  <button onClick={saveLandmarks} disabled={loading} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold shadow-sm">
                    {loading ? '...' : 'Salvează'}
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditingSection('landmarks')} className="text-blue-600 text-sm font-extrabold hover:underline">Editează Repere</button>
              )}
            </div>

            <div className="p-6">
              {editingSection === 'landmarks' ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs text-gray-500 italic">Adaugă puncte de interes (Arena Tenis, Ciric, Palas) pentru vizibilitate maximă.</p>
                    <span className={`text-[10px] font-black px-2 py-1 rounded ${isAtLimit ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                      {landmarksCount} / {maxLandmarks} utilizate
                    </span>
                  </div>

                  {editedVenue.nearby_landmarks.map((landmark, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded-xl border border-gray-100">
                      <input 
                        placeholder="Nume Reper (ex: Arena Open Tenis)" 
                        value={landmark.name}
                        onChange={(e) => handleLandmarkChange(idx, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border-0 bg-transparent text-sm focus:ring-0"
                      />
                      <input 
                        placeholder="Distanță (200m)" 
                        value={landmark.distance}
                        onChange={(e) => handleLandmarkChange(idx, 'distance', e.target.value)}
                        className="w-24 px-3 py-2 border-l bg-transparent text-sm focus:ring-0 text-center font-bold"
                      />
                      <button onClick={() => removeLandmark(idx)} className="text-red-400 p-2 hover:text-red-600 transition-colors">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  ))}

                  {isAtLimit ? (
                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-start gap-3">
                       <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-600 mt-0.5" />
                       <div>
                         <p className="text-sm font-bold text-blue-900">Ai atins limita planului {subscription?.plan.name}</p>
                         <p className="text-xs text-blue-700 mt-1">Upgradează la planul următor pentru a adăuga mai multe repere strategice.</p>
                         <Link to="/subscription" className="text-xs font-black text-blue-600 uppercase mt-2 inline-block hover:underline">Vezi Abonamente</Link>
                       </div>
                    </div>
                  ) : (
                    <button onClick={handleAddLandmark} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-blue-600 hover:border-blue-200 transition-all flex items-center justify-center gap-2 text-sm font-bold">
                      <SafeIcon icon={FiPlus} /> Adaugă Reper Nou
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {venue.nearby_landmarks?.length > 0 ? (
                    venue.nearby_landmarks.map((l, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50/50 p-4 rounded-2xl border border-gray-100 hover:border-blue-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <SafeIcon icon={FiNavigation} className="text-blue-600 h-4 w-4" />
                          </div>
                          <span className="text-sm font-bold text-gray-800">{l.name}</span>
                        </div>
                        <span className="text-[10px] font-black text-gray-400 bg-white px-2 py-1 rounded-md border shadow-sm">{l.distance}</span>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 py-12 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                      <SafeIcon icon={FiTarget} className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-bold text-gray-500">Nu ai repere SEO configurate</p>
                      <p className="text-xs text-gray-400 mt-1">Reperele te ajută să apari în căutări precum "cazare lângă Arena Tenis".</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* INSIGHTS COLUMN */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl">
              <h3 className="font-bold mb-4 flex items-center gap-2 italic">
                <SafeIcon icon={FiTrendingUp} /> Sfat Expert Iași
              </h3>
              <p className="text-sm leading-relaxed opacity-90">
                Proprietățile din Iași care menționează <strong>Arena Open Tenis</strong> sau <strong>Palas</strong> în descriere au o rată de conversie cu 24% mai mare în timpul turneelor și evenimentelor locale.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Statistici Vizibilitate</h4>
                <div className="space-y-4">
                   <div className="flex justify-between items-end">
                      <span className="text-sm text-gray-600">Landmarks SEO</span>
                      <span className="font-bold">{venue.nearby_landmarks?.length || 0} / {maxLandmarks}</span>
                   </div>
                   <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-blue-600 h-full" style={{ width: `${((venue.nearby_landmarks?.length || 0) / maxLandmarks) * 100}%` }}></div>
                   </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VenuePage;